import { cn } from '@invent/wl-ui-kit-next';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleFormField
} from '@invent/wl-ui-kit-next/ui';
import { ArrowRight, Mail, MailCheck, ShieldQuestion } from 'lucide-react';
import * as React from 'react';

import {
  GivenAnswers,
  NumberNotice,
  REDIRECTED_WORDS,
  TeamRoutingQuestion,
  answerRefusal,
  idTypeInputMode,
  idTypeLabel,
  idTypePlaceholder,
  missStatement,
  redirectedNothing
} from '@/domains/auth';
import {
  ADDRESS_HINTS,
  MIGRATED_ACCOUNTS,
  PASSWORD_MIN_LENGTH,
  SCENARIOS,
  screenStatus,
  type JourneyCode,
  type SubjectKind
} from './fixtures';
import {
  PASSWORD_SET_MESSAGE,
  REGISTRATION,
  answerer,
  destinationTitle,
  migratedAccount,
  riaDoorNotice,
  signInAccount,
  surveyTitle,
  type FlowAction,
  type IRunState,
  type IStoredIdentifier
} from './flow';
import {
  Annotation,
  BackLink,
  ChoiceButton,
  ContactBlock,
  MockStatusStrip,
  OutOfStepNotice,
  ProvenanceBadge,
  ScreenShell,
  StepRibbon,
  useStudyChrome
} from './shared';

/**
 * The screens themselves.
 *
 * Each one renders what the respondent sees, plus whether the product has it (the strip, and
 * a `Not built` tag on a half the product lacks); the notes underneath are the review's
 * content and are kept out of the card. Where a screen exists on the server today
 * the copy follows what the API actually answers — the refusals especially, because "this
 * CRD is already registered" and "we could not confirm that number" are the same 409 to a
 * client and two very different things to read.
 */

export interface IScreenProps {
  state: IRunState;
  dispatch: (action: FlowAction) => void;
  /** The chooser hands the flow to another journey, and the URL owns which journey is
   *  running — so this goes through the page rather than through the reducer. `via` is which
   *  chooser: the door's, or the one inside an account. */
  onPickJourney: (journey: JourneyCode, via?: 'door' | 'account') => void;
  /** "Add a survey", on the catalogue: the chooser inside the account, through the page for
   *  the same reason. */
  onAddSurvey: () => void;
  /** Present only when this run was reached through the door, because that is the only case
   *  where there is a screen behind the first one to go back to. */
  onBackToDoor: Nullable<() => void>;
}

/** The server's own steps, which is what the ribbon above every card names — a team's four,
 *  with its route, and everybody else's three. */
function registrationSteps(journey: JourneyCode) {
  const steps = ['anchor', 'confirm_identity', ...(journey === 'teams' ? ['route'] : []), 'complete'];
  return steps.map((code, index) => ({ code, label: `${index + 1} · ${code}` }));
}

/**
 * The screen for the run's step, under the strip saying what it is a mock-up of. One place for
 * the strip, because a screen that had to remember it is a screen that one day would not.
 */
export function FlowScreen(props: IScreenProps) {
  return (
    <>
      <MockStatusStrip mark={screenStatus(props.state)} />
      <StepScreen {...props} />
    </>
  );
}

function StepScreen(props: IScreenProps) {
  const { state } = props;

  switch (state.step) {
    case 'sign_in_entry':
      return <SignInScreen {...props} />;
    case 'choose_subject':
      return <ChooseSubjectScreen {...props} />;
    case 'recover_request':
      return <RecoverRequestScreen {...props} />;
    case 'recover_sent':
      return <RecoverSentScreen {...props} />;
    case 'recover_redeem':
      return <RecoverRedeemScreen {...props} />;
    case 'anchor':
      return <AnchorScreen {...props} />;
    case 'confirm_identity':
      return <ConfirmIdentityScreen {...props} />;
    case 'route':
      return <RouteScreen {...props} />;
    case 'complete':
      return <CompleteScreen {...props} />;
    case 'redirected':
      return <RedirectedScreen {...props} />;
    case 'refused':
      return <RefusedScreen {...props} />;
    case 'run_closed':
      return <RunClosedScreen {...props} />;
    case 'sign_in':
      return <SignInScreen {...props} />;
    case 'forced_change':
      return <PasswordScreen {...props} mode="forced" />;
    case 'activation_notice':
      return <ActivationNoticeScreen {...props} />;
    case 'set_password':
      return <PasswordScreen {...props} mode="activation" />;
    case 'confirm_identifier':
      return <ConfirmIdentifierScreen {...props} />;
    case 'landed':
      return <LandedScreen {...props} />;
    case 'nothing_offered':
      return <NothingOfferedScreen {...props} />;
  }
}

/* ===========================================================================
   The pieces more than one screen is built from
   ======================================================================== */

/** A line carrying a `Not built` tag at its end. */
const TAGGED_LINE = 'flex items-center justify-between gap-2 text-sm text-muted-foreground';

/** What the card's two proposals say when their tag is opened: nothing on the wire carries
 *  either of them. */
const FIRM_NOT_SENT =
  'survey-api sends no firm here: the prompt’s view carries the registry’s name alone, and ' +
  'GET /my/identifiers none at all. A team’s route step is the one screen given a firm — the ' +
  'register firms it routes by, or FINRA’s name where none maps.';

const PAIRED_NOT_SENT =
  'Nothing sends the paired number: NIPR is still a stub (roadmap block 9), and the view has ' +
  'no member for a second identifier.';

/**
 * The record echoed back for a yes or a no.
 *
 * Two screens ask the same question of different data — the registrant's anchor at
 * `confirm_identity`, and a migrated account's stored identifier at first login — and they
 * are deliberately the same card. What is being asked both times is whether a name and a
 * number belong together, and drawing it twice would let one of them quietly become a
 * different question.
 *
 * It draws what the wire carries and tags what it does not. The registration view sends a name
 * and no number, so the prompt passes no `identifier`; the identifier list sends the number,
 * and the name joins from the participant. Neither sends a firm or a paired number: those are
 * the study's proposals, and each carries its `Not built` tag where it is drawn.
 */
function IdentityCard({
  name,
  firmName,
  identifier,
  paired
}: {
  name: Nullable<string>;
  firmName: Nullable<string>;
  /** The number, where the read carries one. */
  identifier?: { idType: string; value: string };
  /** The other number a single NIPR lookup returned with it (FSP-AC-001). */
  paired?: Nullable<{ idType: string; value: string }>;
}) {
  return (
    <div className="mb-6 rounded-md border border-border bg-[var(--color-slight-blue)]/40 p-4">
      {name ? (
        <p className="text-lg text-[var(--color-royal-blue)]">{name}</p>
      ) : (
        <p className="text-sm text-muted-foreground">The record gave no name we can show you.</p>
      )}
      {firmName && (
        <p className={TAGGED_LINE}>
          {firmName}
          <ProvenanceBadge provenance="not-built" note={FIRM_NOT_SENT} />
        </p>
      )}
      {identifier && (
        <p className="mt-2 font-mono text-xs text-muted-foreground">
          {identifier.idType} {identifier.value}
        </p>
      )}
      {paired && (
        <p className={cn(TAGGED_LINE, 'mt-1 font-mono text-xs')}>
          {paired.idType} {paired.value} — found with it
          <ProvenanceBadge provenance="not-built" note={PAIRED_NOT_SENT} />
        </p>
      )}
    </div>
  );
}

/**
 * The address, in the shape the product's own sign-in field has.
 *
 * `type="text"` with an email keyboard rather than `type="email"`, copied from
 * `login-page.tsx` deliberately: survey-api judges the identifier's shape nowhere and the
 * ETL carries legacy `user_name` values across verbatim, so a browser format rule on the
 * sign-in screen would refuse people the server admits — CLAUDE.md's forbidden second
 * evaluator. The two screens that ask for a new address share the field so that they judge
 * nothing either: what refuses an address is the server's answer at the completion, drawn in
 * the field's own error slot.
 */
function EmailField({
  id,
  value,
  error,
  onChange
}: {
  id: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  return (
    <SimpleFormField label="Email" htmlFor={id} required error={error}>
      <Input
        id={id}
        type="text"
        inputMode="email"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="you@firm.com"
        aria-invalid={!!error}
      />
    </SimpleFormField>
  );
}

/**
 * A password, and the policy's answer about it.
 *
 * Three screens choose one — the completion, the redemption and the migrated account's
 * forced change — and all three say the same thing the same way: the first failure in the
 * field's own error slot, the rest listed beneath it, and the length rule as a hint while
 * there is nothing to report. Shared so that the wording cannot drift, because these are
 * the server's rules and not this screen's: what is rendered is what `PasswordPolicy`
 * returned, never a judgement made here.
 */
function PasswordField({
  id,
  label,
  value,
  failures,
  onChange
}: {
  id: string;
  label: string;
  value: string;
  failures: string[];
  onChange: (value: string) => void;
}) {
  return (
    <SimpleFormField label={label} htmlFor={id} required error={failures[0]}>
      <Input
        id={id}
        type="password"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="••••••••••••"
      />
      {failures.length > 1 ? (
        <ul className="mt-1 list-inside list-disc text-xs text-destructive">
          {failures.slice(1).map((failure) => (
            <li key={failure}>{failure}</li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-xs text-muted-foreground">
          At least {PASSWORD_MIN_LENGTH} characters.
        </p>
      )}
    </SimpleFormField>
  );
}

/**
 * "Repeat new password", and the comparison the product makes before sending anything.
 *
 * Not a server rule: `/password/change` and `/password/reset` compare the two fields in their
 * own form and send one password, and the message is theirs. Drawn beside the policy's field
 * because both screens that choose a password after sign-in have it, and a mock-up without it
 * would be a different form from the one it links to.
 */
function RepeatPasswordField({
  id,
  value,
  mismatch,
  onChange
}: {
  id: string;
  value: string;
  mismatch: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <SimpleFormField
      label="Repeat new password"
      htmlFor={id}
      required
      error={mismatch ? 'The two passwords do not match' : undefined}
    >
      <Input
        id={id}
        type="password"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={mismatch}
      />
    </SimpleFormField>
  );
}

/* ===========================================================================
   Registration
   ======================================================================== */

function AnchorScreen({ state, dispatch, onBackToDoor }: IScreenProps) {
  const survey = REGISTRATION[state.journey];
  const [value, setValue] = React.useState('');
  const [idType, setIdType] = React.useState(state.anchorIdType);
  // Outcomes the current switch cannot reach are dropped rather than greyed out: with the
  // providers live, a row saying this number answers `error` contradicts the row above it
  // saying it answers `ok`, and both are true only of different switch positions.
  const hints = SCENARIOS.filter(
    (scenario) =>
      scenario.journey === state.journey &&
      !(scenario.needsStubbedProvider && state.providerMode === 'live')
  );
  const stubbedHere = state.providerMode === 'stubbed' && survey.providerStubbed;
  // The view's `notice`, as `flow.ts` composes it for the wire log.
  const notice = riaDoorNotice(state);

  return (
    <>
      <ScreenShell title={anchorHeading(state)} lede={anchorLede(state)}>
        <StepRibbon steps={registrationSteps(state.journey)} current="anchor" />
        <OutOfStepNotice refusal={state.refusal} />

        {state.message && (
          <Alert className="mb-5">
            <AlertTitle>Check that number</AlertTitle>
            <AlertDescription>{state.message}</AlertDescription>
          </Alert>
        )}

        {notice && (
          <NumberNotice
            notice={notice}
            pending={false}
            onRedirect={() => dispatch({ type: 'redirect' })}
          />
        )}

        <form
          className="grid gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            if (value.trim()) dispatch({ type: 'anchor', value, idType });
          }}
        >
          {survey.anchorIdTypes.length > 1 && (
            <SimpleFormField label="Which number are you entering?" htmlFor="idType" required>
              <Select value={idType} onValueChange={setIdType}>
                <SelectTrigger id="idType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NPN">NAIC NPN number</SelectItem>
                  <SelectItem value="INSURANCE_LICENSE">Insurance licence number</SelectItem>
                </SelectContent>
              </Select>
            </SimpleFormField>
          )}

          <SimpleFormField
            label={idType === 'INSURANCE_LICENSE' ? 'Insurance licence number' : survey.anchorLabel}
            htmlFor="anchor"
            required
            error={state.anchorFailure ?? undefined}
          >
            <Input
              id="anchor"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              // The live field's keyboard, and its example where it shows one — an SEC number,
              // the one shape the server refuses any other value for.
              placeholder={idTypePlaceholder(idType) ?? '0000000'}
              inputMode={idTypeInputMode(idType)}
              autoComplete="off"
              aria-invalid={state.anchorFailure !== null}
            />
          </SimpleFormField>

          <Button type="submit" className="w-full">
            Continue
          </Button>

          {state.journey === 'teams' && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => dispatch({ type: 'no-anchor' })}
            >
              Our lead advisor has no CRD number
            </Button>
          )}

          {onBackToDoor && <BackLink onClick={onBackToDoor} />}
        </form>
      </ScreenShell>

      <Notes>
        {state.journey === 'teams' && (
          <Annotation title="The lead’s CRD first, and a way past it">
            TEAM-AC-001 asked for the team’s name first; the Shook note on TEAM-AC-003 asked for
            the CRD first, “to immediately let the advisor know if it is a dupe”. survey-api took
            the note: the number, then the route, and the name at the last step. A lead with no
            CRD is a real case — <code>/no-anchor</code>, TEAMS alone, goes to the gating
            questions with nothing looked up.
          </Annotation>
        )}
        {onBackToDoor && (
          <Annotation title="Going back from here abandons the run">
            The run was opened by the previous screen’s choice — it carries the survey code —
            so changing the answer means starting another. Back is{' '}
            <code>POST /abandonment</code>, a 204 with no body: the first run ends as{' '}
            <code>abandoned</code>, which is what the staff queue then shows, rather than
            sitting <code>in_progress</code> as somebody who walked away at the anchor.
          </Annotation>
        )}
        {/* First of the notes, and not third: the invented registry holds seven numbers and
            every other one is a not_found by construction, so a reviewer who has to scroll
            past two paragraphs to learn that spends their first attempt on the wrong branch. */}
        {hints.length > 0 && (
          <Annotation title="Numbers this prototype knows">
            <ul className="grid gap-1">
              {hints.map((hint) => (
                <li key={`${hint.label}-${hint.value}`}>
                  <button
                    type="button"
                    className="font-mono text-[var(--color-sapphire)] hover:underline"
                    onClick={() => {
                      setValue(hint.value);
                      if (hint.idType) setIdType(hint.idType);
                    }}
                  >
                    {hint.value}
                  </button>{' '}
                  — {hint.outcome}
                  {hint.needsLiveProvider && state.providerMode === 'stubbed' && (
                    <span className="text-[var(--color-deep-magenta)]">
                      {' '}
                      (needs the providers switched to live)
                    </span>
                  )}
                </li>
              ))}
              <li>
                {stubbedHere ? (
                  <>
                    {survey.registry} is stubbed here as it is in the product, so{' '}
                    <strong>every</strong> number answers <span className="font-mono">error</span>{' '}
                    — including the ones above. What still branches is answered by our own data
                    rather than by a registry.
                  </>
                ) : (
                  <>
                    Anything else answers <span className="font-mono">not_found</span>: the
                    registry here is a table, not a service. The full list is under{' '}
                    <strong>Test data</strong>.
                  </>
                )}
              </li>
            </ul>
          </Annotation>
        )}
        <Annotation title="Why the number comes before the password">
          Legacy asked the other way round and produced 10 362 logins that never filled
          anything in: an account there cost nothing and proved nothing. Roadmap D2.
        </Annotation>
        <Annotation title="Two questions are asked of one number">
          The registry is asked whether the number is real (<code>ok</code> /{' '}
          <code>not_found</code> / <code>error</code>); our own data is asked whether it
          already names a participant and whether anything can log in as that participant. A
          registry outage must not read as “no such advisor”, and a number we already hold
          must not read as a confirmed identity.
        </Annotation>
      </Notes>
    </>
  );
}

/** Who is being registered — `anchor-step.tsx`'s `heading`: a firm, a team, or a person, an
 *  account for one anonymously, one more number of theirs signed in. */
function anchorHeading(state: IRunState): string {
  if (state.journey === 'ria') return 'Register your firm';
  if (state.journey === 'teams') return 'Register your team';
  return state.signedIn ? 'Add your number' : 'Create your account';
}

function anchorLede(state: IRunState): string {
  const number = state.journey === 'teams' ? 'your lead advisor’s CRD number' : 'your number';
  return state.signedIn
    ? `We start with ${number}. It is added to your account at the last step.`
    : `We start with ${number}. Your account is created at the last step.`;
}

/**
 * What the prompt leads with — three sentences, not one with holes in it: the registry's
 * answer, our own record, and a match that came with no name are three different statements.
 * The product's own, with the study's names for the registries.
 */
function confirmIdentityLede(state: IRunState): string {
  const registry = answerer(REGISTRATION[state.journey]);
  if (state.registryName !== null) return `That number is listed at ${registry} under this name.`;
  if (state.displayName !== null) return 'We already hold a record for that number.';
  return `That number matched a record at ${registry}.`;
}

/**
 * The prompt's question and its two answers, as one set. A firm is not a person, and a yes
 * that did not match its question would be answering one the screen did not ask.
 */
function identityWords(journey: JourneyCode) {
  if (journey === 'teams') {
    return {
      title: 'Is this your team’s lead advisor?',
      confirm: 'Yes, that is our lead advisor',
      deny: 'That is not our lead advisor',
      subject: 'your team'
    };
  }
  if (journey === 'ria') {
    return {
      title: 'Is this your firm?',
      confirm: 'Yes, that is my firm',
      deny: 'That is not my firm',
      subject: 'your firm'
    };
  }
  return { title: 'Is this you?', confirm: 'Yes, that’s me', deny: 'That is not me', subject: 'you' };
}

/** Why the confirmation draws no sighting: the product does, and the study's records hold
 *  none. Not survey-api's gap — its `sightings` are shipped (SR-416's B5-11). */
const SIGHTINGS_NOT_DRAWN =
  'The product lists here the teams and firms this CRD is seen on — for FA, with a way to sign in ' +
  'to an account that holds one; for a new team, “Is this your team?”, whose answer joins it ' +
  '(sameTeam). The study’s records hold no CRD another team or firm names, so it draws none.';

/** Why the refusal draws no recovery press: the product does, and the study has no mail. */
const RECOVERY_NOT_DRAWN =
  'The product offers it here: POST …/recovery, an empty 202 every time, queueing the ordinary ' +
  'reset mail to every account that holds the number — naming nobody. The study does not draw it: ' +
  'it sends no mail and has no account to hold the link.';

function ConfirmIdentityScreen({ state, dispatch }: IScreenProps) {
  const linking = state.linkTo !== null;
  const words = identityWords(state.journey);

  return (
    <>
      <ScreenShell title={words.title} lede={confirmIdentityLede(state)}>
        <StepRibbon steps={registrationSteps(state.journey)} current="confirm_identity" />
        <OutOfStepNotice refusal={state.refusal} />

        <IdentityCard
          name={state.registryName ?? state.displayName}
          firmName={state.firmName}
          paired={state.pairedIdentifier}
        />

        {(state.journey === 'fa' || state.journey === 'teams') && !linking && (
          <p className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
            {state.journey === 'fa' ? 'This CRD number also appears on …' : 'Is this your team? …'}
            <ProvenanceBadge provenance="not-built" note={SIGHTINGS_NOT_DRAWN} />
          </p>
        )}

        {linking &&
          (state.journey === 'teams' ? (
            <p className="mb-6 text-sm text-muted-foreground">
              We already hold a team under this lead advisor’s number,{' '}
              <strong className="font-medium">{state.displayName}</strong>, and nobody has signed
              in for it yet. If it is your team, your new account will be linked to it.
            </p>
          ) : (
            <p className="mb-6 text-sm text-muted-foreground">
              Nobody has signed in for this record yet. If it is {words.subject}, your new
              account will be linked to it.
            </p>
          ))}

        <div className="grid gap-3">
          <Button onClick={() => dispatch({ type: 'identity', confirmed: true })}>
            {words.confirm}
          </Button>
          <Button variant="outline" onClick={() => dispatch({ type: 'identity', confirmed: false })}>
            {words.deny}
          </Button>
        </div>
      </ScreenShell>

      <Notes>
        <Annotation title="Two names, and the card shows the registry’s">
          <code>registryName</code> is who the registry says holds the number, on any{' '}
          <code>ok</code> that names one; <code>displayName</code> is a participant we already
          hold, sent on the linking branch alone — the one branch where naming somebody from
          our own records is the point rather than a disclosure, and the reason the “this
          number is taken” refusal says nothing at all. The two need not agree, and where both
          arrive the card shows the registry’s: that is the claim a yes agrees to.
        </Annotation>
        {state.registryName !== null && (
          <Annotation title="A yes here is half a verification">
            The registry named the holder, and the registrant agreeing is the other half: the
            completion stamps <code>verified_at</code> with <code>verified_via = registry</code>.
            An <code>ok</code> that named nobody verifies nothing. A registry that could not be
            asked never brings a new registrant here at all — they go straight to the
            completion, where a notice says the number stays unconfirmed.
          </Annotation>
        )}
        {state.journey === 'teams' && !linking && (
          <Annotation title="The firm is read after the yes">
            Once the lead is confirmed, survey-api reads the firms FINRA BrokerCheck or the SEC’s
            IAPD lists them at on its firm register: a wires firm is the whole answer and the next
            screen is the last; anything else is a few questions. The firm on the card is the
            study’s, as on every prompt.
          </Annotation>
        )}
        <Annotation title="“That is not me” is the step back">
          It is <code>POST /identity {'{'}"confirmed": false{'}'}</code>, and it returns the run to
          the anchor step with everything the previous answer established dropped and the
          attempt count kept — the retry-then-branch rule reads it. A separate “Back” here would
          be the same request under a second name, which is why this screen has two explicit
          answers and no third control.
        </Annotation>
        {linking && (
          <Annotation title="Why linking rather than creating">
            92 participants hold responses and have no account: 54 whose legacy address was
            claimed by another row, 26 team or firm participants whose lead has no account, 11
            stubs and 1 unrecognised user type. Duplicating one of them would split an
            advisor the rankings already know across two records.
          </Annotation>
        )}
      </Notes>
    </>
  );
}

/** The firm the route rests on, as the view names it — `route-step.tsx`'s `firmLine`. */
function firmLine(state: IRunState): Nullable<string> {
  const firmName = state.route?.firmName;
  return firmName ? `Your lead advisor’s firm: ${firmName}.` : null;
}

/**
 * `route` — a new team's questions, one at a time, as the product's route step asks them: the
 * firm line, the answers so far, and the question in `TeamRoutingQuestion`, the product's own
 * rendering of it. Which question comes next is survey-api's, mirrored in `flow.ts`; nothing on
 * this screen knows the tree.
 */
function RouteScreen({ state, dispatch }: IScreenProps) {
  const question = state.question;
  if (!question) return null;
  const firm = firmLine(state);
  const takenBack = state.takenBack;

  return (
    <>
      <ScreenShell
        title="A few questions about your team"
        lede="Your answers decide which survey your team takes."
      >
        <StepRibbon steps={registrationSteps(state.journey)} current="route" />
        <OutOfStepNotice refusal={state.refusal} />

        {firm && (
          <p className="mb-5 rounded-md border border-border bg-[var(--color-slight-blue)]/30 p-3 text-sm">
            {firm}
          </p>
        )}

        <GivenAnswers given={state.given} />

        <TeamRoutingQuestion
          key={`${question.code}|${state.given.length}`}
          question={question}
          initial={takenBack?.question.code === question.code ? takenBack.value : undefined}
          messages={state.answerRefused?.map(answerRefusal)}
          pending={false}
          onAnswer={(answer) => dispatch({ type: 'route', answer })}
        />

        <p className="mt-5 text-xs text-muted-foreground">
          Your answers are kept on this page only — if it is reloaded, the questions start again
          from the first.
        </p>

        <div className="grid">
          {state.given.length > 0 && (
            <BackLink label="Change my last answer" onClick={() => dispatch({ type: 'take-back' })} />
          )}
          <BackLink
            label={state.lookup === null ? 'Enter a CRD number' : 'Enter a different number'}
            onClick={() => dispatch({ type: 'back' })}
          />
        </div>
      </ScreenShell>

      <Notes>
        <Annotation title="The questions are survey-api’s, and so is the order">
          The route step asks what <code>view.question</code> holds and nothing else: SR-416’s
          tree — TEAM-RG-001’s trust company prompt, then TEAM-RG-004…006: a private wealth
          channel, submitting for an RIA, the practice’s structure — lives on the server, which
          also decides where the tree is entered. Each answer posts <strong>the whole set</strong>{' '}
          to <code>/routing</code>, since survey-api keeps none between calls; “Change my last
          answer” posts the set without it. The answers are proposed into TEAM-RG-004…006, locked,
          when the team’s survey is started.
        </Annotation>
        {question.code === 'private_wealth' && (
          <Annotation title="A firm on the wirehouse list">
            BrokerCheck or IAPD lists the lead at one of the wirehouse list’s 21 firms (staff own
            the list), so the tree opens here. Either answer is the Wires Team Survey: the
            question is asked for the response and the ranking, not to route.
          </Annotation>
        )}
        {question.code === 'trust_company' && (
          <Annotation title="Nothing names a firm">
            A lead neither registry lists at a firm, one FINRA does not know, or no CRD at all:
            the tree opens here, at TEAM-RG-001’s “if the lead advisor does not have a CRD”.
          </Annotation>
        )}
      </Notes>
    </>
  );
}

/**
 * `redirected` — a run sent to another survey, both ways: a team whose answers make it an RIA
 * firm, and an RIA firm the SEC does not list that chose to register as a team. The run ended in
 * the same request with nothing created, and the way on is the other survey's own registration,
 * as a new run. `redirected-step.tsx`'s words (`REDIRECTED_WORDS`).
 */
function RedirectedScreen({ state, onPickJourney }: IScreenProps) {
  const words = REDIRECTED_WORDS[state.destination.surveyCode];
  const title = words?.title ?? 'This registration moved to another survey';
  const lede = `${words?.lede ?? ''}${redirectedNothing(state.signedIn)}`;
  const next: JourneyCode = state.destination.surveyCode === 'TEAMS' ? 'teams' : 'ria';
  return (
    <>
      <ScreenShell title={title} lede={lede}>
        <Button
          className="w-full"
          onClick={() => onPickJourney(next, state.signedIn ? 'account' : 'door')}
        >
          {words?.press ?? 'Choose a registration'}
        </Button>
      </ScreenShell>

      <Notes>
        <Annotation title="A redirect is the end of this run">
          survey-api abandons the run as it answers <code>redirected</code>: no account, no
          participant. A tab that comes back to it reads a 409 that names no destination, so the
          product keeps the survey the tab started and says where it went from that. The press
          starts the other survey’s run from its first step — nothing typed here carries into it.
        </Annotation>
      </Notes>
    </>
  );
}

/** The last screen's heading and lead, by mode — `complete-step.tsx`'s own. */
function completeWords(state: IRunState): { title: string; lede: string } {
  return state.signedIn
    ? {
        title: 'Add it to your account',
        lede:
          'Last step — the name we publish under. It is added to the account you are signed ' +
          'in with, so there is no new sign-in to set up.'
      }
    : {
        title: 'Finish your account',
        lede:
          'Last step — how you will sign in, and the name we publish you under. We will mail ' +
          'you a link to confirm your address.'
      };
}

/**
 * What became of the number, for every answer but a confirmed match — `complete-step.tsx`'s
 * own notices, read off `lookup`, `redirected` and `displayName` as the server set them
 * (`displayName` being the linking branch, where a miss drops nothing). For a new
 * registrant `error` is not a step (the plan's R4): survey-api goes from the anchor straight
 * here, and this is where it is said.
 */
function numberNotice(state: IRunState): Nullable<{ title: string; body: string }> {
  const registry = REGISTRATION[state.journey].registry;
  if (state.lookup === 'error') {
    return {
      title: 'We could not confirm that number',
      body:
        `We could not check that number with ${registry}, so it stays unconfirmed. You can ` +
        'carry on with it, or enter a different number.'
    };
  }
  if (state.lookup === 'not_found') {
    return { title: 'No match for that number', body: missConsequence(state) };
  }
  return null;
}

/** What a registry miss means for the account: redirected, linked or dropped, in that order —
 *  `complete-step.tsx`'s `missConsequence`, over the study's run. */
function missConsequence(state: IRunState): string {
  const missed = missStatement(REGISTRATION[state.journey].surveyCode);
  if (state.redirected) return `${missed}, so this account will not start the survey you chose.`;
  if (state.displayName !== null) {
    return (
      `${missed}. It is on the record you confirmed, and your account will be linked to ` +
      'that record.'
    );
  }
  return state.signedIn
    ? `${missed}, so the survey will be added to your account without it.`
    : `${missed}, so your account will be created without it.`;
}

/** The tags' notes for the completion's two proposals, which the product's form lacks. */
const PREFILL_NOT_BUILT =
  'The product leaves these fields empty: the view carries the registry’s name as one ' +
  'string, and splitting it into a first and a last would be this app deciding a name.';

const TERMS_NOT_BUILT =
  'survey-api’s completion takes no terms acceptance, and the product’s form has no checkbox.';

function CompleteScreen({ state, dispatch }: IScreenProps) {
  const survey = REGISTRATION[state.journey];
  const [email, setEmail] = React.useState(state.email ?? '');
  const [password, setPassword] = React.useState('');
  const [terms, setTerms] = React.useState(false);
  const [fields, setFields] = React.useState<Record<string, string>>(() =>
    prefillFields(state)
  );

  const needsTerms = state.journey === 'fsp';
  const refusal = state.refusal?.reason === 'address_unavailable' ? state.refusal : null;
  const notice = numberNotice(state);
  // Only where a registry named the holder of what the field names: FINRA names a team's
  // lead, never the team.
  const pulled = state.registryName !== null && state.journey !== 'teams';
  const revise = () => dispatch({ type: 'back' });
  const words = completeWords(state);

  return (
    <>
      <ScreenShell title={words.title} lede={words.lede}>
        <StepRibbon steps={registrationSteps(state.journey)} current="complete" />
        <OutOfStepNotice refusal={state.refusal} />

        {notice && (
          <Alert className="mb-5">
            <AlertTitle>{notice.title}</AlertTitle>
            <AlertDescription>
              <p>{notice.body}</p>
              <Button type="button" variant="outline" size="sm" className="mt-3" onClick={revise}>
                Enter a different number
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <form
          className="grid gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            dispatch({ type: 'complete', email, password, fields });
          }}
        >
          {/* Not marked required, as in the product: a new participant needs *a* name part,
              one we already hold needs none, and which is the server's to say. */}
          {survey.fields.map((field) => (
            <SimpleFormField key={field.name} label={field.label} htmlFor={field.name}>
              <Input
                id={field.name}
                value={fields[field.name] ?? ''}
                onChange={(event) =>
                  setFields((current) => ({ ...current, [field.name]: event.target.value }))
                }
              />
              {pulled && (
                <p className={cn(TAGGED_LINE, 'mt-1 text-xs')}>
                  From {answerer(survey)}. Correct it if it is wrong.
                  <ProvenanceBadge provenance="not-built" note={PREFILL_NOT_BUILT} />
                </p>
              )}
            </SimpleFormField>
          ))}

          {/* Signed in, survey-api makes no account and ignores an address or password, so
              neither is asked for. */}
          {!state.signedIn && (
            <>
              <EmailField
                id="email"
                value={email}
                onChange={setEmail}
                error={refusal ? 'That address cannot be used. Try a different one.' : undefined}
              />

              <PasswordField
                id="password"
                label="Password"
                value={password}
                failures={state.passwordFailures}
                onChange={setPassword}
              />
            </>
          )}

          {needsTerms && (
            <div className="flex items-start justify-between gap-2">
              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  checked={terms}
                  onCheckedChange={(checked) => setTerms(checked === true)}
                  className="mt-0.5"
                />
                <span>I have read and agree to the Veritant Terms &amp; Conditions.</span>
              </label>
              <ProvenanceBadge provenance="not-built" note={TERMS_NOT_BUILT} />
            </div>
          )}

          <Button type="submit" disabled={needsTerms && !terms}>
            {state.signedIn ? 'Add to my account' : 'Create my account'}
          </Button>

          {/* Where no notice already offers it: the way back is the same request either way. */}
          {!notice && (
            <BackLink
              label={state.lookup === null ? 'Enter a CRD number' : 'Enter a different number'}
              onClick={revise}
            />
          )}
        </form>

        {refusal && (
          <ContactBlock
            reference={state.runId}
            what={
              'If it is the only address you have, send us this reference and the address ' +
              'you tried, and we will get you an account.'
            }
          />
        )}
      </ScreenShell>

      <Notes>
        <Annotation title="The way back is a revision">
          “Enter a different number” is <code>POST /revision</code>: the run returns to the
          anchor with everything the last number established dropped — the lookup, both names,
          a redirect it earned — and the attempts kept, the reset “that is not me” makes. It is
          the only way back from this step, and it is offered once: in the notice where there is
          one, under the form where there is not.
        </Annotation>
        {state.lookup === 'error' && !state.linkTo && (
          <Annotation title="“We could not confirm” is a notice, not a step">
            For a new registrant survey-api goes from a registry that could not be asked straight
            to this screen (<code>ForNewParticipant</code>): nothing was confirmed and nothing was
            denied, so there is nobody to ask “is this you?” about. The number is stored
            unverified, and the staff queue is what makes that survivable rather than invisible.
          </Annotation>
        )}
        <Annotation title="One transaction, or nothing — and no response">
          The account, the participant, the link and the identifier are written together, and a
          new team’s route with them as its routing. <strong>No response is opened</strong>, for
          any survey: the account lands on the catalogue, where the survey it registered for is
          offered to start. Inside an account the transaction adds to it instead, and mails
          nothing.
        </Annotation>
        <Annotation title="The number is checked once more, here">
          Minutes passed since the anchor step and nothing in the database refuses a second{' '}
          <code>account_participant</code> row. This read is what stands between one advisor
          and two logins; the race that slips past it is caught by{' '}
          <code>UNIQUE(id_type, value)</code> inside the transaction, with the same answer.
        </Annotation>
        <Annotation title="What you type here arrives in the survey as a proposal">
          When the survey is started, its first response proposes it: <code>origin =
          prefill</code>, <code>source = registration</code>, <code>state = proposed</code> —
          editable, and marked “From your registration”. Matched by usage code:{' '}
          {survey.fields
            .map((field) => field.usageCode)
            .filter(Boolean)
            .join(', ') || 'none — no Teams usage asks a team’s name'}
          .
        </Annotation>
        {pulled && (
          <Annotation title="The pre-filled names are the study’s, not the product’s" tone="gap">
            Three sheets say the name should be pulled from the registry and locked, and the same
            sheets say the registrant confirms it by pop-up. The study pulls it and leaves it
            editable (roadmap D5: locking is always available later, un-locking after somebody
            is stuck is not). The product pulls nothing into this form — the registrant types the
            name, and it is that typed name survey-api proposes into the survey.
          </Annotation>
        )}
        {refusal?.deadEnd && (
          <Annotation
            title="This person’s way out is staff’s, and nothing here says so"
            tone="gap"
          >
            One of the 54, refused in the words a duplicate gets — so a different address gets
            them through, and the only address they have does not. With that one they cannot
            recover a password either, because it resolves to the other account. Since block 6
            staff can let them in from the participant card — link the account that holds the
            address, or create a seat at another one and link that — but this screen cannot say
            so without naming whose the address is, and there is no channel to send them to
            (the support gap).
          </Annotation>
        )}
        <Annotation title="Addresses that refuse">
          <ul className="grid gap-1">
            {ADDRESS_HINTS.map((hint) => (
              <li key={hint.email}>
                <button
                  type="button"
                  className="font-mono text-[var(--color-sapphire)] hover:underline"
                  onClick={() => setEmail(hint.email)}
                >
                  {hint.email}
                </button>{' '}
                — {hint.outcome}
              </li>
            ))}
            <li>
              A password under 12 characters, or one containing the address’s name, is refused
              by the policy with the rules that failed — never with the password.
            </li>
          </ul>
        </Annotation>
      </Notes>
    </>
  );
}

/** The registry's name, split the way the study proposes (`PREFILL_NOT_BUILT`); nothing
 *  where no registry named anybody, or named only a team's lead. */
function prefillFields(state: IRunState): Record<string, string> {
  const name = state.registryName;
  if (!name || state.journey === 'teams') return {};
  if (state.journey === 'ria') return { firmName: name };
  const parts = name.split(' ');
  return { legalFirstName: parts[0] ?? '', legalLastName: parts[parts.length - 1] ?? '' };
}

/**
 * `409 registration-refused`, `identifier_unavailable` — the product's screen, with the one
 * half it deliberately lacks drawn under it and tagged: a way to reach a person. The reference
 * is the product's, printed in full outside that block.
 */
function RefusedScreen({ state, dispatch }: IScreenProps) {
  return (
    <>
      <ScreenShell
        title="We cannot continue with that number"
        lede="That number is not available for registration, so this registration has ended."
      >
        <dl className="mb-6 rounded-md border border-border bg-[var(--color-slight-blue)]/30 p-4">
          <dt className="text-xs text-muted-foreground">Reference</dt>
          <dd className="mt-1 font-mono text-xs break-all select-all">{state.runId}</dd>
        </dl>

        <Button variant="outline" className="w-full" onClick={() => dispatch({ type: 'restart' })}>
          Start again
        </Button>

        <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
          Send a sign-in link to the account that holds this number
          <ProvenanceBadge provenance="not-built" note={RECOVERY_NOT_DRAWN} />
        </p>

        <ContactBlock
          what={
            'Send us the number you entered and this reference, and we will check who the ' +
            'record belongs to.'
          }
        />
      </ScreenShell>

      <Notes>
        <Annotation title="What the registrant is told, and what staff sees">
          The body carries <code>identifier_unavailable</code> and nothing else. What actually
          happened is in <code>workflow_run_event</code>: <em>{state.refusal?.staffDetail}</em>
        </Annotation>
        <Annotation title="Why the run ends rather than offering a retry">
          The anchor is the one thing this registrant cannot change, so a second attempt
          would be the same refusal. The address refusal is the opposite and leaves the run
          in progress.
        </Annotation>
        <Annotation title="Nowhere to send them, and we are the ones who know who they are" tone="gap">
          Two screens tell somebody to contact the Veritant team — this one, and the completion
          when an address is refused — and neither can say how: there is no support address in
          this app, no contact route on survey-api, and nothing that reads a mail sent back.
          It is client content nobody has supplied, and the product leaves the line out rather
          than point at a channel that does not exist.
        </Annotation>
        <Annotation title="The reference is the run id, and printing it gives nothing away">
          The refusal is careful to name nobody, so the temptation is to print nothing at all —
          but the run id is the one thing this registrant already holds, it identifies no
          person, and it is what <code>GET /admin/registrations</code> is keyed on. Without it
          the queue shows staff an anonymous refusal at 09:12 and the caller has nothing to
          quote.
        </Annotation>
      </Notes>
    </>
  );
}

/**
 * `409 registration-out-of-step`, run `abandoned`, read by a tab still holding the run — the
 * product's `RunClosed`, word for word.
 */
function RunClosedScreen({ dispatch }: IScreenProps) {
  return (
    <>
      <ScreenShell
        title="This registration was closed"
        lede="It was cancelled, or left unfinished for too long, and cannot be picked up again."
      >
        <Button className="w-full" onClick={() => dispatch({ type: 'restart' })}>
          Start a new registration
        </Button>
      </ScreenShell>

      <Notes>
        <Annotation title="One answer for three endings">
          A cancel from another tab, the sweep after seven days untouched, and the refusal that
          abandoned the run all read back the same: <code>409</code> out of step, run{' '}
          <code>status: abandoned</code>. A client cannot tell them apart and does not try — one
          screen, true of all three.
        </Annotation>
        <Annotation title="Why a week">
          survey-api’s <code>StaleRunAge</code>. Registration is one sitting for most people, but
          the one who stops to find their CRD and comes back after the weekend is exactly who
          the flow is for, and a run ended under them starts over. The sweep is what keeps{' '}
          <code>in_progress</code> in the staff queue meaning somebody is still working on it.
        </Annotation>
      </Notes>
    </>
  );
}

/* ===========================================================================
   Migrated accounts
   ======================================================================== */

function SignInScreen({ state, dispatch }: IScreenProps) {
  const account = signInAccount(state.journey);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');

  return (
    <>
      <ScreenShell title="Sign in" lede="Complete and update your ranking survey.">
        <form
          className="grid gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            dispatch({ type: 'sign-in', email, password });
          }}
        >
          <EmailField id="login-email" value={email} onChange={setEmail} />

          <SimpleFormField label="Password" htmlFor="login-password" required>
            <Input
              id="login-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••••"
            />
          </SimpleFormField>

          {state.message && (
            <p
              role="alert"
              className={
                state.message === PASSWORD_SET_MESSAGE
                  ? 'text-sm font-medium text-[var(--color-teal)]'
                  : 'text-sm font-medium text-destructive'
              }
            >
              {state.message}
            </p>
          )}

          <Button type="submit">Sign in</Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          <button
            type="button"
            onClick={() => dispatch({ type: 'recover' })}
            className="hover:text-[var(--color-royal-blue)]"
          >
            Forgot your password?
          </button>
        </p>

        <div className="mt-6 border-t border-border pt-6 text-center text-sm">
          <p className="text-muted-foreground">New to the Veritant portal?</p>
          <Button
            variant="outline"
            className="mt-2 w-full"
            onClick={() => dispatch({ type: 'create-account' })}
          >
            Create an account
          </Button>
        </div>
      </ScreenShell>

      <Notes>
        <Annotation title="One door, five ways through it">
          Everybody arrives here: the 9 716 who have a password, the 45 336 who will arrive
          from a link instead, and everybody registering for the first time. The way out to
          registration is “Create an account” under the form, which the product’s{' '}
          <code>/login</code> carries into <code>/registration</code>.
        </Annotation>
        <Annotation title="The sign-in half is the product’s, unchanged">
          The login identifier carries no format rule on purpose: the ETL brings legacy’s{' '}
          <code>user_name</code> across verbatim and it is sometimes a bare username.
        </Annotation>
        <Annotation title="Credentials that work here">
          <button
            type="button"
            className="font-mono text-[var(--color-sapphire)] hover:underline"
            onClick={() => {
              setEmail(account.email);
              setPassword(account.password ?? '');
            }}
          >
            {account.email} / {account.password}
          </button>{' '}
          {state.journey === 'unentitled' ? (
            <>
              — an account whose participant has never answered a questionnaire. It signs in
              and changes its password like any other migrated account; what it finds behind
              that is what this journey is for.
            </>
          ) : (
            <>
              — one of the 9 716 whose legacy password survived our policy. The other 45 336
              have no password at all; that is the journey below this one.
            </>
          )}
        </Annotation>
      </Notes>
    </>
  );
}

/**
 * "Email me a link" — `/password/forgot`, which the product built once survey-api could mail
 * what the request mints (its SR-213, block 5). Until then `/login` linked straight to the
 * redemption screen, because a request form that cannot deliver anything lies twice. Its copy
 * is the product's, which is what the strip's link promises.
 */
function RecoverRequestScreen({ state, dispatch }: IScreenProps) {
  const [email, setEmail] = React.useState(state.email ?? '');

  return (
    <>
      <ScreenShell
        title="Reset your password"
        lede="Tell us the address you sign in with and we will email you a link to choose a new password."
      >
        <form
          className="grid gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            if (email.trim()) dispatch({ type: 'request-reset', email });
          }}
        >
          <EmailField id="recover-email" value={email} onChange={setEmail} />

          <Button type="submit">Email me a link</Button>

          <button
            type="button"
            onClick={() => dispatch({ type: 'open-redeem' })}
            className="text-sm text-muted-foreground hover:text-[var(--color-royal-blue)]"
          >
            I already have a reset code
          </button>

          <BackLink label="Back to sign in" onClick={() => dispatch({ type: 'back' })} />
        </form>
      </ScreenShell>

      <Notes>
        <Annotation title="In the product since the mail transport">
          survey-api’s worker mails the link now, so <code>/login</code>’s “Forgot your
          password?” leads here rather than straight to the redemption. Before that, a form
          promising a link would have been the one thing worse than no form.
        </Annotation>
        <Annotation title="The test inbox is the product’s, and not drawn here">
          Everywhere but production the request also takes <code>deliverTo</code> — a tester
          types an advisor’s address and names their own inbox to receive the mail instead. The
          product draws it, marked dev / UAT only, where <code>GET /auth/mail-delivery</code>{' '}
          says <code>redirect</code>; a study of what a respondent sees leaves it out.
        </Annotation>
        <Annotation title="202 for every address, known or not">
          Anything else turns this into a test for whether somebody has an account on the
          platform. The confirmation screen therefore cannot say “we found you”, and must not
          try to.
        </Annotation>
        <Annotation title="A person handing you a code still works">
          An admin issues a seven-day token (SR-208) and passes it on out of band. That is what
          “I already have a reset code” is for, and it is why the redemption screen keeps its
          field editable rather than only reading a link.
        </Annotation>
      </Notes>
    </>
  );
}

/** The 202, said in a way that does not claim to know whether the address exists. */
function RecoverSentScreen({ state, dispatch }: IScreenProps) {
  return (
    <>
      <ScreenShell
        title="Check your inbox"
        lede={
          <>
            If <strong>{state.email}</strong> belongs to an account, a link to choose a new
            password is on its way. It works once, and expires within the hour.
          </>
        }
      >
        <div className="grid gap-3">
          <p className="text-sm text-muted-foreground">
            Nothing after a few minutes? Check your spam folder, or ask again — a new link
            replaces the old one.
          </p>
          <Button variant="outline" onClick={() => dispatch({ type: 'open-redeem' })}>
            I have a reset code
          </Button>
          <BackLink label="Back to sign in" onClick={() => dispatch({ type: 'back' })} />
        </div>
      </ScreenShell>

      <Notes>
        <Annotation title="Sent by the worker, if the deployment names a transport">
          The route answered 202 and queued a job; survey-api’s worker mints the token and
          mails it through MailJet. A deployment whose <code>Mail:Transport</code> is{' '}
          <code>none</code> drops the mail and says so in its log — which this screen cannot
          tell apart, and does not try to.
        </Annotation>
        <Annotation title="An hour, not seven days">
          A self-requested link lives <code>Auth:Tokens:ResetLifetime</code>, an hour; the
          seven days belong to the admin-issued token. Asking again invalidates the older link,
          so “ask again” is always safe advice.
        </Annotation>
        <Annotation title="“If it belongs to an account” is doing real work">
          The sentence is hedged because the server’s answer is: 202 comes back for an address
          nobody holds exactly as for one somebody does. A screen saying “we have emailed you”
          would leak the thing the status code was careful not to.
        </Annotation>
      </Notes>
    </>
  );
}

/** `/password/reset` — shipped, and the only way anybody actually gets back in today. */
function RecoverRedeemScreen({ state, dispatch }: IScreenProps) {
  const [token, setToken] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [repeat, setRepeat] = React.useState('');
  const [mismatch, setMismatch] = React.useState(false);

  return (
    <>
      <ScreenShell
        title="Set a new password"
        lede="Paste the reset code your administrator gave you, then choose a password."
      >
        <form
          className="grid gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            const differs = password !== repeat;
            setMismatch(differs);
            if (!differs) dispatch({ type: 'redeem-reset', token, password });
          }}
        >
          <SimpleFormField label="Reset code" htmlFor="token" required>
            <Input
              id="token"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              placeholder="From your reset link"
              autoComplete="off"
            />
          </SimpleFormField>

          <PasswordField
            id="reset-password"
            label="New password"
            value={password}
            failures={state.passwordFailures}
            onChange={setPassword}
          />

          <RepeatPasswordField
            id="reset-password-repeat"
            value={repeat}
            mismatch={mismatch}
            onChange={setRepeat}
          />

          <Button type="submit">Set password</Button>
          <BackLink label="Back to sign in" onClick={() => dispatch({ type: 'back' })} />
        </form>
      </ScreenShell>

      <Notes>
        <Annotation title="This screen is shipped, and it is the whole recovery story today">
          It takes the token from the URL <strong>fragment</strong> and keeps the field editable
          for a code pasted by hand — an admin-issued reset is handed over out of band, so there
          may be no link at all. <code>#token=</code> and never <code>?token=</code>: nginx logs
          in the default combined format, and the query form would write a live single-use
          credential into the server log.
        </Annotation>
        <Annotation title="It issues no session">
          204 and nothing else, which is why this ends on the sign-in screen rather than inside
          the app. A reset that signed you in would make a link in an inbox a session.
        </Annotation>
        <Annotation title="A rejected password does not spend the token">
          SR-208’s own fix: the policy is checked before the token is consumed, so somebody
          whose first attempt is too short still has their one code.
        </Annotation>
      </Notes>
    </>
  );
}

/**
 * The product's four, in its words and its order (`choose-registration.tsx`). Canada and French
 * Canada never collected a CRD and are not registrable in either.
 */
const SUBJECT_OPTIONS: { journey: JourneyCode; label: string; hint: string }[] = [
  {
    journey: 'fa',
    label: 'I am a financial advisor',
    hint: 'You will need your CRD number.'
  },
  {
    journey: 'fsp',
    label: 'I am an insurance professional',
    hint: 'You will need your NPN or your insurance licence number.'
  },
  {
    journey: 'ria',
    label: 'I am registering an RIA firm',
    hint: 'You will need the firm’s SEC number.'
  },
  {
    journey: 'teams',
    label: 'I am registering my team',
    hint: 'You will need your lead advisor’s CRD number.'
  }
];

/** The four, and what the screen adds under them — the way back. */
function SubjectChoices({
  onPick,
  children
}: {
  onPick: (journey: JourneyCode) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-3">
      {SUBJECT_OPTIONS.map((option) => (
        <ChoiceButton
          key={option.journey}
          label={option.label}
          hint={option.hint}
          onClick={() => onPick(option.journey)}
        />
      ))}
      {children}
    </div>
  );
}

/**
 * "What are you registering?" — `/registration`'s first screen, and the one no run exists
 * behind yet.
 *
 * `POST /auth/registrations` takes a survey code, and nothing derives one: FA and TEAMS both
 * anchor on a CRD, so the number cannot tell an advisor from a team, and the anchor is asked
 * after this point anyway. Either something asks, or the code arrives in the link that
 * brought the registrant here. Inside an account ("Add a survey") the same four choices add a
 * survey to it, and the way back is to its surveys.
 */
function ChooseSubjectScreen({ state, dispatch, onPickJourney }: IScreenProps) {
  const signedIn = state.signedIn;

  return (
    <>
      <ScreenShell
        title={signedIn ? 'What are you adding?' : 'What are you registering?'}
        lede={
          signedIn
            ? 'It is added to the account you are signed in with — no new sign-in is made. ' +
              'This decides which ranking survey it is, and what we ask you for next.'
            : 'This decides which ranking survey you are filling in, and what we ask you for next.'
        }
      >
        <SubjectChoices
          onPick={(journey) => onPickJourney(journey, signedIn ? 'account' : 'door')}
        >
          <BackLink
            label={signedIn ? 'Back to your surveys' : 'Back to sign in'}
            onClick={() => dispatch({ type: 'back' })}
          />
        </SubjectChoices>
      </ScreenShell>

      <Notes>
        <Annotation title="Why something has to ask">
          <code>POST /auth/registrations</code> takes a survey code and nothing derives one —
          and it cannot be derived later either, because <strong>FA and TEAMS both anchor on a
          CRD</strong>. The same number is an advisor registering themselves or a team
          registering its lead. The product asks it at <code>/registration</code>, which opens
          on this question and holds no run until it is answered (survey-api’s D9).
        </Annotation>
        <Annotation title="Or it never appears, because the link already said" tone="gap">
          Veritant reaches advisors by invitation, and an invitation can carry the survey code —
          in which case this screen is skipped and the registrant lands straight on the anchor
          for the right survey. The two answers differ in who may register at all: a chooser
          is open to anyone who finds the page, a link is not. <strong>Needs a ruling</strong>,
          and it is upstream of any invitation mail either way.
        </Annotation>
        {signedIn && (
          <Annotation title="The same registration, inside the account">
            <code>/portal/surveys/add</code> is the registration page in its signed-in mode: every
            request carries the session’s bearer, so survey-api binds the run to this account,
            and the completion adds to it — no address, no password, no mail. Any session binds,
            staff’s included, so the product draws “Add a survey” for an advisor alone.
          </Annotation>
        )}
        <Annotation title="Canada is not on it">
          Canada and French Canada have no ACCOUNT CREATION section at all and never collected a
          CRD, so they are not registrable; their cycles stay visible as historical (roadmap
          D1).
        </Annotation>
        <Annotation title="A team is not a survey the registrant picks twice">
          Choosing “my team” starts at the lead’s CRD, and where it lands — Wires, Independent
          Practices or the RIA firm survey — is survey-api’s: the answers to TEAM-RG-001 and
          TEAM-RG-004…006, entered by the lead’s firm on the wirehouse list, not this screen.
        </Annotation>
      </Notes>
    </>
  );
}

function ActivationNoticeScreen({ state, dispatch }: IScreenProps) {
  const account = MIGRATED_ACCOUNTS.activation;

  return (
    <>
      <div className="flex justify-center bg-[var(--color-off-white)] px-6 py-10">
        <div className="w-full max-w-md">
          <p className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
            <Mail className="size-4" aria-hidden /> The message that would reach them
          </p>

          <div className="rounded-lg border border-border bg-card shadow-sm">
            <div className="border-b px-6 py-3 text-xs text-muted-foreground">
              To: {account.email}
              <br />
              Subject: Your Veritant account is ready
            </div>
            <div className="grid gap-4 px-6 py-6 text-sm">
              <p>{account.displayName},</p>
              <p>
                Your advisor record has moved to the new Veritant portal, and your past
                survey answers came with it. Set a password to get in.
              </p>
              <Button
                className="justify-self-start"
                onClick={() => dispatch({ type: 'redeem-token' })}
              >
                Set my password
              </Button>
              <p className="text-xs text-muted-foreground">
                This link expires in 7 days. If it has, ask us for another one.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Notes>
        <Annotation title="Nothing sends this" tone="gap">
          survey-api mails four things since block 5 — a reset somebody asked for, a new seat,
          and two notices — and this is none of them. 45 336 accounts are waiting on it; each
          can ask for a reset link at <code>/password/forgot</code>, but nothing tells them to.
          Roadmap B3 is the campaign.
        </Annotation>
        <Annotation title="A campaign is not a reset" tone="gap">
          The admin reset mints a seven-day token for hand delivery, which is right for one
          person and wrong for 45 336: a batch wants its own lifetime, throttled sending,
          bounce and complaint handling, and a way to see who has landed.
        </Annotation>
        <Annotation title="The link carries the token in the fragment, not the query">
          <code>/password/reset#token=…</code>. nginx logs in the default <code>combined</code>{' '}
          format, so a query parameter would write a live single-use credential into the
          server log. That part is shipped.
        </Annotation>
      </Notes>
    </>
  );
}

function PasswordScreen({
  state,
  dispatch,
  mode
}: IScreenProps & { mode: 'forced' | 'activation' }) {
  const [current, setCurrent] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [repeat, setRepeat] = React.useState('');
  const [mismatch, setMismatch] = React.useState(false);
  const forced = mode === 'forced';

  return (
    <>
      <ScreenShell
        title={forced ? 'Choose a new password' : 'Set your password'}
        lede={
          forced
            ? `${state.email ?? 'This account'} needs a new password before going any further. ` +
              'Until then, every other page is closed to this account.'
            : 'One password, and you are in.'
        }
      >
        <form
          className="grid gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            const differs = password !== repeat;
            setMismatch(differs);
            if (differs) return;
            dispatch({
              type: 'set-password',
              password,
              currentPassword: forced ? current : undefined
            });
          }}
        >
          {forced && (
            <SimpleFormField label="Current password" htmlFor="current-password" required>
              <Input
                id="current-password"
                type="password"
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
              />
            </SimpleFormField>
          )}

          <PasswordField
            id="new-password"
            label="New password"
            value={password}
            failures={state.passwordFailures}
            onChange={setPassword}
          />

          <RepeatPasswordField
            id="new-password-repeat"
            value={repeat}
            mismatch={mismatch}
            onChange={setRepeat}
          />

          {state.message && (
            <p role="alert" className="text-sm font-medium text-destructive">
              {state.message}
            </p>
          )}

          <Button type="submit">{forced ? 'Change password' : 'Set password'}</Button>
        </form>
      </ScreenShell>

      <Notes>
        <Annotation title="The forced change is shipped">
          <code>must_change_password</code> is true on all 55 054 migrated accounts and{' '}
          <code>password_changed_at</code> is null on every one — not one has completed a first
          login. survey-api answers 403 everywhere but <code>/auth/me</code> until it clears,
          and checks the current password before the policy: a wrong one is an empty 401.
        </Annotation>
        <Annotation title="The policy is the server's — and this screen is the exception">
          Length, a blocklist and “must not contain the address’s name”. <strong>In the
          product</strong> the screen renders the failures the server returned and judges
          nothing; here there is no server, so the study mirrors the policy in{' '}
          <code>flow.ts</code> to have something to show. That copy is the one deliberate
          second evaluator in the study and must not be carried into the real form. The two
          fields' match is different — the product's own form checks it, and so does this one.
        </Annotation>
      </Notes>
    </>
  );
}

/* ===========================================================================
   The identifier question — the product's card, word for word
   ======================================================================== */

/** `identifier-confirmation-card.tsx`'s title and description, which the screen and the
 *  landing's card both draw. */
const IDENTIFIER_QUESTION = 'Is this number yours?';

function identifierLede(holder: Nullable<string>): string {
  return `${holder ? `We hold it for ${holder}.` : 'We hold it on your record.'} Your surveys ` +
    'stay open whichever you answer.';
}

/**
 * The number and the two answers, as the product's card draws them. The firm is the study's
 * proposal and tagged: neither `GET /my/identifiers` nor `GET /my/participants` carries one.
 */
function IdentifierQuestion({
  identifier,
  firmName,
  dispatch
}: {
  identifier: Pick<IStoredIdentifier, 'idType' | 'value'>;
  firmName?: string;
  dispatch: React.Dispatch<FlowAction>;
}) {
  return (
    <>
      <dl className="mb-6 grid gap-1 rounded-md border border-border bg-[var(--color-slight-blue)]/40 p-4">
        <dt className="text-sm text-muted-foreground">{idTypeLabel(identifier.idType)}</dt>
        <dd className="font-mono text-lg text-[var(--color-royal-blue)]">{identifier.value}</dd>
        {firmName && (
          <dd className={cn(TAGGED_LINE, 'mt-1')}>
            {firmName}
            <ProvenanceBadge provenance="not-built" note={FIRM_NOT_SENT} />
          </dd>
        )}
      </dl>

      <div className="flex flex-wrap gap-3">
        <Button onClick={() => dispatch({ type: 'confirm-identifier', confirmed: true })}>
          Yes, that’s mine
        </Button>
        <Button
          variant="outline"
          onClick={() => dispatch({ type: 'confirm-identifier', confirmed: false })}
        >
          That is not mine
        </Button>
      </div>
    </>
  );
}

/** What the product's catalogue says once a number is answered in the visit that answered it
 *  — the card's `AnswerNote`, the denial saying that it clears nothing. */
function IdentifierAnswerNote({ identifier }: { identifier: IStoredIdentifier }) {
  const number = `${idTypeLabel(identifier.idType)} ${identifier.value}`;
  const denied = identifier.selfConfirmation === 'denied';

  return (
    <Alert className="mb-4">
      <AlertDescription>
        {denied
          ? `Noted — you told us ${number} is not yours. Nothing has been removed or changed, ` +
            'and your surveys stay open.'
          : `Thank you — you told us ${number} is yours.`}
      </AlertDescription>
    </Alert>
  );
}

function ConfirmIdentifierScreen({ state, dispatch }: IScreenProps) {
  const account = migratedAccount(state.journey);

  return (
    <>
      <ScreenShell title={IDENTIFIER_QUESTION} lede={identifierLede(account.displayName)}>
        <IdentifierQuestion
          identifier={account.identifier}
          firmName={account.firmName}
          dispatch={dispatch}
        />
      </ScreenShell>

      <Notes>
        <Annotation title="An answer, not a verification">
          The confirmation writes <code>self_confirmation</code> and never{' '}
          <code>verified_at</code>: survey-api ruled (its Q4) that a respondent’s own word is not
          a corroboration. That column means something other than the owner checked the number
          — a registry, or staff reading one — and the staff queues read it to find who still
          needs a look, so a self-click stamping it would empty them of exactly the migrated
          rows worth working.
        </Annotation>
        <Annotation title="A denial clears nothing">
          Not the number, not a verification, not the responses the rankings were built from —
          and it blocks nothing: the survey stays open. It is a fact for staff to look at,
          recorded as an <code>identifier_self_denied</code> audit event, and not a gate.
        </Annotation>
        <Annotation title="Not a gate, so not a screen of its own" tone="gap">
          survey-api ruled that an unanswered confirmation blocks nothing, and an interstitial
          in front of the catalogue would be this app inventing the gate the server declined to
          have. The product asks it as a card on the catalogue instead — one per number nobody
          has been asked about, with these words and these two answers — which is where the
          strip above links. It is drawn as a screen here so the journey has a place to show
          it; the landing after a registration shows the card in place.
        </Annotation>
      </Notes>
    </>
  );
}

/* ===========================================================================
   Where it all ends: the scoped catalogue
   ======================================================================== */

/** `SubjectKind` in the words the screen shows — the thing the scoped catalogue keys on. */
const SUBJECT_KIND_LABEL: Record<SubjectKind, string> = {
  person: 'Person',
  org: 'Organisation'
};

/** What `/portal/surveys/add` added, said once on the catalogue it lands on — the product's
 *  `AddedNotice`; a redirected completion says where it was sent instead. */
function AddedNotice({ added }: { added: NonNullable<IRunState['added']> }) {
  return (
    <Alert className="mb-4" role="status">
      <AlertTitle>{added.redirected ? 'Registration sent on' : 'Survey added'}</AlertTitle>
      <AlertDescription>
        {added.redirected
          ? `It was registered for ${surveyTitle(added.destinationSurvey)} instead of ` +
            `${surveyTitle(added.survey)}.`
          : `${surveyTitle(added.survey)} is now on your account.`}
      </AlertDescription>
    </Alert>
  );
}

function LandedScreen({ state, dispatch }: IScreenProps) {
  const title = destinationTitle(state.destination);
  const kindLabel = SUBJECT_KIND_LABEL[state.destination.subjectKind];
  // The two blocks under the card talk to the reviewer — what else the catalogue does not
  // offer, and what the registration left on the record — and the product's catalogue has
  // neither. With the chrome closed the strip calls this a mock-up of the live `/`, so they
  // go with the rest of the commentary rather than sit under that claim.
  const chrome = useStudyChrome();
  // A first login lands here too, from the door or a migrated journey, and its participant
  // and responses predate the visit: nothing about them was created on the way.
  const migrated =
    state.journey === 'entry' || state.journey === 'returning' || state.journey === 'activation';

  return (
    <>
      <div className="bg-[var(--color-off-white)] px-6 py-10">
        <div className="mx-auto w-full max-w-2xl">
          <div className="mb-6 flex items-center justify-between gap-3">
            <div>
              <p className="text-xs text-muted-foreground">Signed in as {state.email}</p>
              <h1 className="text-2xl text-[var(--color-royal-blue)]">Your surveys</h1>
            </div>
            <Badge variant="outline">{kindLabel}</Badge>
          </div>

          {state.added && <AddedNotice added={state.added} />}

          {/* An account that came in through a mailed link has confirmed its address by
              redeeming it; every other one is asked — as sent only right after an anonymous
              registration, the one completion that mails the link. */}
          {state.journey !== 'activation' && state.email && (
            <ConfirmAddressPrompt email={state.email} sent={!migrated && !state.signedIn} />
          )}

          {state.storedIdentifier &&
            (state.storedIdentifier.selfConfirmation === null ? (
              <div className="mb-4 rounded-lg border border-border bg-card p-6 shadow-sm">
                <p className="text-lg text-[var(--color-royal-blue)]">{IDENTIFIER_QUESTION}</p>
                <p className="mt-1 mb-4 text-sm text-muted-foreground">
                  {identifierLede(state.storedIdentifier.holder)}
                </p>
                <IdentifierQuestion identifier={state.storedIdentifier} dispatch={dispatch} />
              </div>
            ) : (
              <IdentifierAnswerNote identifier={state.storedIdentifier} />
            ))}

          <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-lg text-[var(--color-royal-blue)]">{title}</p>
                <p className="mt-1 text-sm text-muted-foreground">2026 cycle · open until 31 March</p>
              </div>
              {/* Start, never Continue: registration opens no response, so the survey is
                  offered and not yet begun. */}
              <Button>
                Start <ArrowRight className="size-4" />
              </Button>
            </div>

            <p className="mt-4 border-t pt-4 text-sm text-muted-foreground">
              {state.destination.why}
            </p>
          </div>

          {chrome && (
            <>
              <div
                className={cn(
                  'mt-4 rounded-lg border border-dashed p-4 text-sm',
                  'text-muted-foreground'
                )}
              >
                <p className="flex items-center gap-2 font-medium text-[var(--color-royal-blue)]">
                  <ShieldQuestion className="size-4" aria-hidden />
                  Nothing else is offered
                </p>
                <p className="mt-1">
                  The other three rankings are not on this list. One is added by registering its
                  number inside the account — the product’s “Add a survey”, under the list.
                </p>
              </div>

              <dl className="mt-6 grid gap-2 rounded-lg border bg-card p-4 text-sm">
                <Row label="Participant">
                  {migrated
                    ? `${migratedAccount(state.journey).displayName} — the migrated record`
                    : state.linkTo
                      ? `${state.linkTo.displayName} — the existing record, now linked`
                      : state.signedIn && state.destination.subjectKind === 'person'
                        ? 'The account’s one person, now holding the number — or a new one, ' +
                          'where the account acts for none or several'
                        : `${kindLabel}, created by this registration`}
                </Row>
                <Row label="Identifier">
                  {state.storedIdentifier ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs">
                        {state.storedIdentifier.idType} {state.storedIdentifier.value}
                      </span>
                      <Badge
                        variant="outline"
                        className={
                          state.storedIdentifier.verified
                            ? 'border-emerald-300 text-emerald-900'
                            : 'border-amber-300 text-amber-900'
                        }
                      >
                        {state.storedIdentifier.verified ? 'verified' : 'unverified'}
                      </Badge>
                      {/* A different question from the badge before it, and never the same
                          column: the owner's word, which verifies nothing. */}
                      {state.storedIdentifier.selfConfirmation && (
                        <Badge variant="outline">
                          owner {state.storedIdentifier.selfConfirmation}
                        </Badge>
                      )}
                    </span>
                  ) : (
                    'None stored — the registry had no record and an unrecognised number is ' +
                    'worse than none.'
                  )}
                </Row>
                <Row label="Response">
                  {migrated
                    ? 'The migrated responses, as they were: a first login changes a password ' +
                      'and asks about a number, and writes to no response.'
                    : 'None yet. Registration opens no response; Start creates it, and ' +
                      'proposes what registration collected.'}
                </Row>
                {state.routing && (
                  <Row label="Routing">
                    {`${state.routing.outcome} · by the team’s answers${
                      state.routing.firm ? ` · ${state.routing.firm.label}` : ''
                    }`}
                  </Row>
                )}
              </dl>
            </>
          )}

          <Button variant="outline" className="mt-6" onClick={() => dispatch({ type: 'restart' })}>
            Run it again
          </Button>
        </div>
      </div>

      <Notes>
        <Annotation title="Asked again after a registration" tone="gap">
          A registration that stored a number lands on the product's identifier card, even when
          the registrant said yes at <code>confirm_identity</code> a minute earlier:{' '}
          <code>CompleteRegistrationService</code> leaves <code>self_confirmation</code> null,
          and the owner ruled that the card shows as the server answers rather than behind a
          filter of this app's. It stops asking once survey-api records the first answer (the
          registration-flow plan's ask 1).
        </Annotation>
        <Annotation title="The address is asked about, never required">
          The completion mails a link to confirm the address it was given (survey-api's block
          6), and the product says so in a prompt under its header — here at the top, since
          this landing draws no header. It gates nothing: an unconfirmed address blocks neither
          sign-in nor a survey. A migrated account is asked the same, in plainer words, and one
          that set its password through a mailed link is not asked at all — redeeming that link
          proved the address. The product draws the prompt on every screen until the address
          is confirmed or put away with “Not now”; its buttons ask survey-api for a new link,
          and here they do nothing.
        </Annotation>
        <Annotation title="The catalogue is scoped, and this is what it keys on">
          Not a role: what the participant has answered, the numbers it holds, and the
          registrations it completed. A participant that answered a survey in any cycle is
          entitled to that survey; so is one holding a number the survey anchors on — a CRD for
          FA, an NPN or a licence for FSP, an SEC number for RIA, a lead CRD or a routing for a
          team — and so is one whose registration for it completed, which is why a registrant
          FINRA did not find is still offered FA. A redirected run makes nothing to entitle.
        </Annotation>
        {state.journey === 'teams' && (
          <Annotation title="A team starts on the path survey-api routed">
            The start card reads the team’s <code>teamRouting</code> off{' '}
            <code>/my/cycles</code> and names the routed path, and its create sends{' '}
            <strong>no pathCode</strong>: survey-api fills it. A team whose routing is unknown is
            sent to answer its own questions first, and one routed to the RIA firm survey is told
            so, with “Register the firm”.
          </Annotation>
        )}
        <Annotation title="“Add a survey” sits under the list">
          The product draws it for an advisor on every catalogue, empty or not: another number
          registered inside the account. This landing leaves it out; the empty catalogue, where
          it is the only way on, draws it.
        </Annotation>
        <Annotation title="Which participant a start is for, without guessing">
          An account acting for two participants — 11 531 migrated accounts do, always org +
          person — gets one entry per participant, and each cycle names which of them it is
          for in <code>eligibleParticipants</code>. It narrows the choice rather than making
          it: a participant it names may still be refused <code>response-exists</code>, its one
          live slot in that cycle being spent.
        </Annotation>
        <Annotation title="Closed cycles stay visible, and refuse">
          Canada and French Canada have no registration at all and are rendered as historical
          rather than hidden. The other half now holds too: opening a response in a closed
          cycle is refused <code>409 cycle-closed</code>, and so is every write to a draft the
          close caught — its own refusal rather than <code>response-not-writable</code>,
          because “your answers are locked” and “this cycle is over” are two different screens.
        </Annotation>
      </Notes>
    </>
  );
}

/**
 * The product's prompt to confirm an address (`email-verification-banner.tsx`), in its words —
 * right after a registration it says a link is on its way, which the completion queued, and
 * otherwise that the address is unconfirmed. Its two buttons are drawn and do nothing: the
 * product's ask survey-api for another link, and this study calls nothing. The test inbox the
 * product draws for that request on a test environment is not drawn, as on every screen here.
 */
function ConfirmAddressPrompt({ email, sent }: { email: string; sent: boolean }) {
  const address = <strong className="break-words">{email}</strong>;
  return (
    <div className="mb-4 flex items-start gap-3 rounded-lg border border-border bg-[var(--color-slight-blue)]/40 p-4 text-sm">
      <MailCheck className="mt-0.5 size-5 shrink-0 text-[var(--color-royal-blue)]" aria-hidden />
      <div className="grid min-w-0 flex-1 gap-2">
        <p className="font-medium text-[var(--color-royal-blue)]">
          {sent ? 'Check your inbox to confirm your email address' : 'Confirm your email address'}
        </p>
        <p className="text-muted-foreground">
          {sent ? (
            <>We have mailed a link to {address}. Opening it confirms the address is yours.</>
          ) : (
            <>We have not yet confirmed that {address} is yours. The link we mail there does it.</>
          )}{' '}
          Nothing waits on it — everything here works either way.
        </p>
        <div>
          <Button type="button" size="sm" variant="outline">
            Send a new link
          </Button>
        </div>
      </div>
      <Button type="button" variant="ghost" size="sm" className="shrink-0">
        Not now
      </Button>
    </div>
  );
}

/**
 * Signed in, and the catalogue is empty.
 *
 * Every way a *registration* can be refused has a screen, and this is not a refusal. Nothing
 * went wrong — the account is real, and `GET /my/cycles` correctly answered `[]`. 10 586 of the
 * 55 056 migrated accounts arrive here behind a forced password change, and so does a firm
 * whose registration was redirected to the team survey, which entitles nothing.
 *
 * The **card** is the product's, word for word from `my-surveys-page.tsx` — its sentence and its
 * way out, "Add a survey": the four registrations, made inside the account (`/portal/surveys/add`). What
 * the product has no answer for is the person none of the four describes, and that one button
 * carries its `Not built` tag in place.
 */
/** A block heading with its `Not built` tag pushed to the right. */
const TAGGED_HEADING = cn(
  'flex items-center justify-between gap-2 font-medium',
  'text-[var(--color-royal-blue)]'
);

/** `my-surveys-page.tsx`'s `ADD_SENTENCE`, beside the card's own. */
const ADD_SENTENCE =
  'Register another number — your own, your team’s lead advisor’s or your firm’s — and its ' +
  'survey is added to this account. No new sign-in is made.';

function NothingOfferedScreen({ state, dispatch, onAddSurvey }: IScreenProps) {
  const answered = state.message !== null;

  return (
    <>
      <div className="bg-[var(--color-off-white)] px-6 py-10">
        <div className="mx-auto w-full max-w-2xl">
          <div className="mb-6">
            <p className="text-xs text-muted-foreground">Signed in as {state.email}</p>
            <h1 className="text-2xl text-[var(--color-royal-blue)]">Your surveys</h1>
          </div>

          {state.added && <AddedNotice added={state.added} />}

          <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
            <p className="text-lg text-[var(--color-royal-blue)]">Nothing to fill in yet</p>
            <p className="mt-2 text-sm text-muted-foreground">
              No ranking survey is open for this account. Which surveys appear is decided from
              what our records hold about the people and firms this account speaks for — a
              ranking answered before, or a number on file that one is built from.{' '}
              {ADD_SENTENCE}
            </p>
            <Button variant="outline" className="mt-4" onClick={onAddSurvey}>
              Add a survey
            </Button>
          </div>

          {/* The one answer the product lacks, marked where it is drawn — and its answer too:
              the same missing route, one click on. */}
          {answered ? (
            <div className="mt-4 rounded-lg border border-dashed p-6">
              <p className={TAGGED_HEADING}>
                Nothing on file connects you to a ranking
                <ProvenanceBadge provenance="not-built" />
              </p>
              <p className="mt-2 text-sm text-muted-foreground">{state.message}</p>

              <Button
                variant="outline"
                className="mt-6"
                onClick={() => dispatch({ type: 'restart' })}
              >
                Run it again
              </Button>
            </div>
          ) : (
            <div className="relative mt-4">
              <ChoiceButton
                label="None of these describes me"
                hint="Says so, and stops asking."
                onClick={() => dispatch({ type: 'no-subject' })}
                className="pr-28"
              />
              <ProvenanceBadge provenance="not-built" className="absolute top-3 right-3" />
            </div>
          )}
        </div>
      </div>

      <Notes>
        <Annotation title="The card is the product’s, and it is not a failure">
          The catalogue read is shipped and its answer is right: this participant has answered
          no questionnaire and holds no number a ranking anchors on. 13 971 participants are
          in that position on the migrated target, 13 967 of them carrying a{' '}
          <code>LEGACY_USER_ID</code> and an address and nothing else. The sentence above is
          the one <code>my-surveys-page.tsx</code> renders, so the two cannot drift.
        </Annotation>
        <Annotation title="“Add a survey” is registration, inside the account">
          <code>/portal/surveys/add</code>: the same four choices and steps as{' '}
          <code>/registration</code>, each request carrying the session’s bearer so survey-api
          binds the run to this account, and a completion that adds to it — a person’s number
          joins the account’s one person, a firm or a team is a new participant it acts for. No
          address, no password, no mail. Drawn for an advisor alone: any session binds a run,
          staff’s included.
        </Annotation>
        <Annotation title="“None of these” has to land somewhere" tone="gap">
          Nothing records that answer today, so the population that gives it stays invisible to
          staff. It is the cheapest thing on this screen to build: the catalogue’s own emptiness
          is already measurable, but a person saying “I am none of these” is a fact nobody can
          derive from the database.
        </Annotation>
      </Notes>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] items-start gap-3">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * The commentary column under a screen — always outside the card, and only when the study's
 * chrome is open. With it closed the page is the screen alone, which is the state to show
 * somebody being asked "is this the right screen"; the reasoning behind it is a different
 * question and gets a different mode.
 */
function Notes({ children }: { children: React.ReactNode }) {
  if (!useStudyChrome()) return null;
  return <div className="mx-auto grid max-w-2xl gap-2 px-6 pb-10">{children}</div>;
}
